import React, { useRef } from 'react';

import { Box, Group, Tooltip } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import FirstClassVisitBadge from 'components/status-badges/FirstClassVisitBadge';
import { AthleteRoster } from 'types';

export const FIRST_CLASS_VISIT_TOOLTIP = 'Attending their first class.';
export const VISITOR_TOOLTIP = 'Visitor.';
export const COMBINED_TOOLTIP = 'This athlete is a Visitor attending their first class.';

export const VISITOR_STATUS = 'Visitor';

export type ReservationAlertBadge = 'visitor' | 'first_class_visit';

interface ReservationAlertBadgesProps {
  athleteStatus?: AthleteRoster['athleteStatus'];
  isFirstClassVisit?: boolean;
  /** Called once per badge per mount, the first time a coach hovers or taps it. */
  onBadgeViewed?: (badge: ReservationAlertBadge) => void;
}

// Tooltips need `touch` explicitly: Mantine's default is hover only, and the Staff Kiosk runs
// on tablets. Same shape as InvoicesTable.tsx.
const TOOLTIP_EVENTS = { hover: true, focus: false, touch: true } as const;

const ReservationAlertBadges = ({
  athleteStatus,
  isFirstClassVisit = false,
  onBadgeViewed,
}: ReservationAlertBadgesProps): JSX.Element | null => {
  // Mantine's Tooltip clones its child to attach its own handlers, so the wrapper below is
  // outside it. The ref keeps this to one event per badge for as long as the row stays mounted.
  const viewedBadges = useRef<Set<ReservationAlertBadge>>(new Set());

  const isVisitor = athleteStatus === VISITOR_STATUS;

  if (!isVisitor && !isFirstClassVisit) {
    return null;
  }

  const isCombined = isVisitor && isFirstClassVisit;

  const handleBadgeViewed = (badge: ReservationAlertBadge): void => {
    if (viewedBadges.current.has(badge)) {
      return;
    }
    viewedBadges.current.add(badge);
    onBadgeViewed?.(badge);
  };

  return (
    <Group spacing={6} noWrap>
      {/* Visitor first, then First Class Visit, per the spec's Deliverables table. */}
      {isVisitor && (
        <Box
          component='span'
          data-testid='visitor-badge-wrapper'
          onMouseEnter={() => handleBadgeViewed('visitor')}
        >
          <Tooltip
            label={isCombined ? COMBINED_TOOLTIP : VISITOR_TOOLTIP}
            withArrow
            withinPortal
            events={TOOLTIP_EVENTS}
            multiline={isCombined}
            width={isCombined ? 240 : undefined}
          >
            {/* Badge components do not forward refs and Tooltip needs one, so it anchors to a
                plain element instead. Same shape as InvoicesTable.tsx. */}
            <span style={{ display: 'inline-flex' }}>
              <AthleteStatusBadge
                status={VISITOR_STATUS}
                variant='light'
                size='lg'
                radius='xl'
                leftSection={<IconUser size={12} style={{ display: 'block' }} />}
                sx={{ textTransform: 'none', fontWeight: 500 }}
                aria-label={`Visitor. ${isCombined ? COMBINED_TOOLTIP : VISITOR_TOOLTIP}`}
              />
            </span>
          </Tooltip>
        </Box>
      )}
      {isFirstClassVisit && (
        <Box
          component='span'
          data-testid='first-class-visit-badge-wrapper'
          onMouseEnter={() => handleBadgeViewed('first_class_visit')}
        >
          <Tooltip
            label={isCombined ? COMBINED_TOOLTIP : FIRST_CLASS_VISIT_TOOLTIP}
            withArrow
            withinPortal
            events={TOOLTIP_EVENTS}
            multiline
            width={240}
          >
            <span style={{ display: 'inline-flex' }}>
              <FirstClassVisitBadge
                aria-label={`First Class Visit. ${
                  isCombined ? COMBINED_TOOLTIP : FIRST_CLASS_VISIT_TOOLTIP
                }`}
              />
            </span>
          </Tooltip>
        </Box>
      )}
    </Group>
  );
};

export default ReservationAlertBadges;
